/**
 * Sentry custom-span helpers.
 *
 * Spans created here attach to the active pageload/navigation transaction and
 * inherit its sampling decision (no extra config). Use a dot-namespaced `op`
 * so spans group cleanly in Explore > Traces (e.g. `app.init.*`, `page.init.*`).
 */
import * as Sentry from '@sentry/sveltekit';

/**
 * Run `fn` inside a Sentry span and return its result.
 * The span ends when `fn` settles. Synchronous and async `fn` are both supported.
 * @template T
 * @param {string} op - Dot-namespaced operation, e.g. `app.init.experiments`.
 * @param {string} name - Human-readable span name.
 * @param {(span: import('@sentry/core').Span | undefined) => T} fn - Work to measure; receives the span so it can set attributes.
 * @param {Record<string, string | number | boolean>} [attributes] - Attributes known up front.
 * @returns {T}
 */
export function instrument(op, name, fn, attributes) {
	// Defensive: if tracing is unavailable (e.g. partially-mocked Sentry in
	// tests), run the work directly so instrumentation never alters behavior.
	if (typeof Sentry.startSpan !== 'function') {
		return fn(undefined);
	}
	return Sentry.startSpan({ name, op, attributes }, fn);
}
